import SegmentCardHelper from '@/guides/p2-guide-details/guide-settings/segment-card/segment-card-helper';
import {
    BRANCHING_EDGES_TYPES, BRANCHING_NODES_TYPES, ENGAGEMENT_GOAL_TYPES,
    MAX_CONDITION_CHAIN, MAX_MESSAGES, MESSAGE_SIDE_BLADE_CONFIG, MESSAGE_TYPES, NO_MESSAGES_INTERACTION,
} from '@/orchestrate/journeys/constants';
import { EMAIL_CONTENT_SOURCE } from '@/emails/constants';
import { PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { STATUS_TYPES } from '@/orchestrate/journeys/status.constants';
import moment from '@/utils/moment-wrapper';
import i18nDateTimeFormatter from '@/utils/i18n/i18n-datetime-formatter';
import { canCreateJourney } from '@/vuex/modules/journeys/journeys-permissions.utils';
import { filtersHaveRulesOfKind } from '@/vuex/modules/segments/segment.helpers';
import dagre from '@dagrejs/dagre';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import maxBy from 'lodash/maxBy';
import uniqBy from 'lodash/uniqBy';
import { i18n_t as $t } from '@/utils/i18n';

import { useProductLinesStore } from '@/stores/product-lines.store';

import { useAuthStore } from '@/stores/auth.store';

const { START, EXIT, MESSAGE, CONDITION } = BRANCHING_NODES_TYPES;
const { REGULAR, YES, NO } = BRANCHING_EDGES_TYPES;
const legacyAppId = -323232;
const { GUIDE, EMAIL } = MESSAGE_TYPES;
const { AWS, HUBSPOT, MARKETO, ELOQUA } = PROVIDER_TYPES;

export const NODES_PATH_MAP = { [YES]: 1, [REGULAR]: 2, [NO]: 3 };

export function journeyDefaultName () {
    return `New Journey ${i18nDateTimeFormatter.formatTimestampLabel()}`;
}

export function getMessageSidebladeConfig (messageType, emailContentSource) {
    const base = MESSAGE_SIDE_BLADE_CONFIG[messageType];

    if (!base) return null;

    if (messageType !== EMAIL) return base;

    const contentSourceConfig = base.byContentSource[emailContentSource]
        ?? base.byContentSource[EMAIL_CONTENT_SOURCE.BLOCKS];

    return { ...base, ...contentSourceConfig };
}

export async function buildJourney ({
    audience,
    audienceUiHint,
    app,
    name,
    description,
    mlRecommended,
    target,
    recommendation,
}) {
    return journeyFactory({
        appId: app.id,
        name,
        description,
        audience,
        audienceUiHint,
        mlRecommended,
        goal:
            target && !isEmpty(target)
                ? {
                    itemType: target.itemType || target.kind,
                    itemId: target.itemId || target.id,
                    type: ENGAGEMENT_GOAL_TYPES.APPLICATION,
                }
                : null,
        recommendation,
    });
}

export function journeyFactory ({
    appId = legacyAppId,
    name,
    description,
    audience = [],
    audienceUiHint = { filters: [] },
    mlRecommended = false,
    goal = null,
    recommendation = {},
}) {
    return {
        name: name || journeyDefaultName(),
        description,
        status: STATUS_TYPES.DRAFT,
        appId,
        audience,
        audienceUiHint,
        mlRecommended,
        ...(mlRecommended && { recommendation }),
        goal,
        messageGraph: {
            nodes: [],
            edges: [],
        },
    };
}

export async function buildAudienceAndAudienceUiHint (segment, targetApp) {
    const targetSegment = segment;
    const isCustomTargetSegment = targetSegment.id === 'custom';

    // Identify focus group rules for audience targeting page
    if (targetSegment.isFocusGroup) {
        targetSegment.definition.filters.forEach((filter) => {
            filter.isFocusGroup = true;
        });
    }

    const audienceUiHint = isCustomTargetSegment
        ? { filters: get(targetSegment, 'definition.filters', []) }
        : { filters: [{ kind: 'visitor', segmentId: targetSegment.id }] };

    const segmentOptions = isCustomTargetSegment ? get(targetSegment, 'definition.advancedOptions') : {};

    const advancedOptions = {
        ...segmentOptions,
        showGuidesToAnon: get(targetApp, 'applicationFlags.showGuidesToAnon'),
        segmentNotOnSourceSub: true,
    };

    // if the segment is a custom segment and has nested segment
    // then build the audience with the buildCustomAudiencePipeline
    // else build the audience with the buildAudiencePipeline
    const audience =
        isCustomTargetSegment && filtersHaveRulesOfKind(audienceUiHint.filters, 'segment')
            ? await SegmentCardHelper.buildCustomAudiencePipeline(targetSegment)
            : await SegmentCardHelper.buildAudiencePipeline(audienceUiHint.filters, advancedOptions);

    return { audience, audienceUiHint };
}

export function* messagesGenerator ({ messageGraph } = {}) {
    if (!messageGraph?.nodes) {
        return;
    }

    for (const node of messageGraph.nodes) {
        if (node.nodeType === MESSAGE) {
            yield node.message;
        }
    }
}

export function getAllGuideMessagesByJourney ({ messageGraph } = {}) {
    const messages = [];
    for (const message of messagesGenerator({ messageGraph })) {
        if (message.type === GUIDE) {
            messages.push(message);
        }
    }

    return messages;
}

export function getAllGuidesIdsByJourney ({ messageGraph } = {}) {
    return getAllGuideMessagesByJourney({ messageGraph }).map(({ id }) => id);
}

export function createMessage (id, type = GUIDE, duration = 1) {
    return {
        type,
        id,
        durationInDays: duration,
    };
}

// Returns a 0–1 ratio for the shared rate formatter (formatRatePercent).
export function calculatePercentage (currentValue, maxValue) {
    return maxValue === 0 ? 0 : currentValue / maxValue;
}

export function buildAnalyticsRow (
    { label = '', prefix = '', type = '', provider = '', id = '', appId = '' },
    analyticsDataObj = {},
) {
    const achievedGoal = analyticsDataObj[`${prefix}Adopted`] || 0;
    const messageInteraction = analyticsDataObj[`${prefix}Interaction`];
    const messageClicked = analyticsDataObj[`${prefix}Clicked`];
    const enteredJourney = analyticsDataObj.totalEnteredJourney || 0;

    const notAvailableValue = '--';
    const formatMessageActionValue = (value) => (value === undefined ? notAvailableValue : value || 0);

    return {
        id,
        appId,
        type,
        provider,
        label,
        messageInteraction: formatMessageActionValue(messageInteraction),
        messageClicked: formatMessageActionValue(messageClicked),
        achievedGoal,
        percentageThatAchievedGoal: calculatePercentage(achievedGoal, enteredJourney),
    };
}

export function buildAnalyticsMessage ({ id, appId, name, type, index, provider }) {
    return {
        id,
        type,
        appId,
        prefix: `message${index}`,
        label: name,
        provider,
    };
}

export function buildJourneyAnalyticsTableData (analyticsData, messages = []) {
    return [
        ...messages.map(({ id, appId, name, type, provider }, index) => {
            return buildAnalyticsRow(buildAnalyticsMessage({ id, appId, name, type, index, provider }), analyticsData);
        }),
        buildAnalyticsRow(NO_MESSAGES_INTERACTION, analyticsData),
    ];
}

export function areJourneyMessageNamesLoaded (messages = []) {
    return messages.every(({ name }) => Boolean(name?.trim()));
}

export function isScheduled ({ schedulingSettings = {} }) {
    const { startDate = 0 } = schedulingSettings;

    return startDate > Date.now();
}

export function hasExternalProvidersMessages (messages) {
    return messages.some(({ provider }) => isExternalProvider(provider));
}

export function isExternalProvider (provider) {
    return [HUBSPOT, MARKETO, ELOQUA].includes(provider);
}

export function getMessageIconName ({ type, provider }) {
    switch (type) {
        case GUIDE:
            return GUIDE.toUpperCase();
        case EMAIL:
            if ([AWS, HUBSPOT, MARKETO, ELOQUA].includes(provider)) {
                return provider.toUpperCase();
            }
            break;
        case CONDITION:
            return CONDITION.toUpperCase();
        case START:
            return START.toUpperCase();
        case EXIT:
            return EXIT.toUpperCase();
        default:
            return '';
    }
}

export function timeSeriesRange (timeSeries) {
    const { first, count } = timeSeries;
    const start = moment(first);

    return {
        start: start.valueOf(),
        end: start.add(count, 'days').valueOf(),
    };
}

export function getDurationInDays (messages) {
    return messages.reduce((acc, { durationInDays }) => acc + durationInDays, 0);
}

export function getNodeMessageIndex (node, messages) {
    return messages.findIndex((message) => message.id === node.data.id);
}

const DEFAULT_CARD_WIDTH = 263;
const DEFAULT_CARD_HEIGHT = 82;
const METRIC_ROW_HEIGHT = 22;
const START_EXIT_CARD_HEIGHT = 35;
const EMPTY_FLOW_START_CARD_HEIGHT = 108;
const TRIGGER_RULES_HEIGHT_EXTRA = 46;

export function getNodeSizes (node) {
    const {
        isMetricCard,
        emptyFlow,
        value,
        hasTriggerRules,
    } = node.data || {};
    const sizesType = node.nodeType !== MESSAGE ? node.nodeType || GUIDE : node.data.type;
    const metricRowCount = Array.isArray(value) ? value.length : 0;

    let sizes;

    if (!isMetricCard) {
        const messageHeight = DEFAULT_CARD_HEIGHT + (hasTriggerRules ? TRIGGER_RULES_HEIGHT_EXTRA : 0);
        sizes = {
            [START]: { width: DEFAULT_CARD_WIDTH, height: emptyFlow ? EMPTY_FLOW_START_CARD_HEIGHT : START_EXIT_CARD_HEIGHT },
            [EXIT]: { width: DEFAULT_CARD_WIDTH, height: START_EXIT_CARD_HEIGHT },
            [CONDITION]: { width: DEFAULT_CARD_WIDTH, height: DEFAULT_CARD_HEIGHT },
            [EMAIL]: { width: DEFAULT_CARD_WIDTH, height: messageHeight },
            [GUIDE]: { width: DEFAULT_CARD_WIDTH, height: messageHeight },
        };
    } else {
        const startExitMetricHeight = START_EXIT_CARD_HEIGHT + (metricRowCount * METRIC_ROW_HEIGHT);
        const messageMetricHeight =
            DEFAULT_CARD_HEIGHT +
            metricRowCount * METRIC_ROW_HEIGHT;

        sizes = {
            [START]: { width: DEFAULT_CARD_WIDTH, height: startExitMetricHeight },
            [EXIT]: { width: DEFAULT_CARD_WIDTH, height: startExitMetricHeight },
            [CONDITION]: { width: DEFAULT_CARD_WIDTH, height: DEFAULT_CARD_HEIGHT },
            [EMAIL]: { width: DEFAULT_CARD_WIDTH, height: messageMetricHeight },
            [GUIDE]: { width: DEFAULT_CARD_WIDTH, height: messageMetricHeight },
        };
    }

    return sizes[sizesType];
}

function attachDummyNodeAndEdge (graph, graphDepth, sourceNodePath, targetNodeId) {
    const sourceConditionLevel = sourceNodePath.length;
    const fakeMinlen = (graphDepth - sourceConditionLevel + 1) * 3;
    graph.setNode(`${targetNodeId}_dummyNode`,{ width: 1, height: 0 });
    graph.setEdge(targetNodeId, `${targetNodeId}_dummyNode`, { minlen: fakeMinlen });
}
function ballanceConditionNodes (nodes, edges) {
    /* search Yes/No edges that does an equivalent edge (due to manual edge/connection line that is not represented in edges array) */
    /* if such edge found - create dummy twin node to fix condition child position (it needs 2 outgoing edges from condition node preserve aceptable graph layout) */
    for (const edge of edges) {
        const { edgeType } = edge.data;

        if(isYesNoEdge(edgeType)) {
            const targetNode = nodes.find((node) => node.id === edge.target);
            const targetNodePathSuffix = parseInt(targetNode.path.slice(-1));
            const twinNodePathSuffix = 4 - targetNodePathSuffix; // 4 - 3 === 1 | 4 - 1 === 3
            const twinNodePath = targetNode.path.slice(0, -1) + twinNodePathSuffix;
            const twinNode = nodes.find((node) => node.path === twinNodePath);

            /* if there is no twin node here it means that a manual edge/connection line is connected to a condition node */
            /* create dummy twin node to fix condition child position (it needs 2 outgoing edges */
            if(!twinNode) {
                const dummyTwinNodeId = `${edge.target}_dummy_node`;
                const eqvivalentEdge = {
                    source: edge.source,
                    target: dummyTwinNodeId,
                    data: { edgeType: edgeType === NO ? YES : NO },
                };

                const dummyTwinNode = {
                    id: dummyTwinNodeId,
                    path: twinNodePath,
                };

                /* insert the newly created edge and node in the correct index */
                const edgeIndex = edges.findIndex(({ id }) => id === edge.id);
                const indexToInsertEdgeAt = eqvivalentEdge.data.edgeType === NO ? edgeIndex + 1 : edgeIndex;
                edges.splice(indexToInsertEdgeAt, 0, eqvivalentEdge);

                const targetNodeIndex = nodes.findIndex(({ id }) => id === targetNode.id);
                const indexToInsertNodeAt = eqvivalentEdge.data.edgeType === NO ? targetNodeIndex + 1 : targetNodeIndex;
                nodes.splice(indexToInsertNodeAt, 0, dummyTwinNode);
            }
        }
    }
}

export function getNodesWithPosition (nodes, edges, containerWidth) {
    const graph = new dagre.graphlib.Graph();

    // Set an object for the graph label
    // nodesep horizontal spacing between nodes was set to 64 to enable room for manual edge routing
    graph.setGraph({ nodesep: 100, ranksep: 42, edgesep: 0, marginx: 32, marginy: 32, labelpos: 'c' });

    // Default to assigning a new object as a label for each new edge.
    graph.setDefaultEdgeLabel(() => ({}));

    const graphDepth = maxBy(nodes, (node) => node.path.length).path.length;

    ballanceConditionNodes(nodes, edges);

    for (const node of nodes) {
        const metricsNodeSizes = getNodeSizes(node);
        graph.setNode(node.id, {
            ...node,
            width: node.width || metricsNodeSizes.width,
            height: node.height || metricsNodeSizes.height,
        });
    }

    for (const edge of edges) {
        const edgeType = edge.data?.edgeType || REGULAR;
        let minlen = edgeType === REGULAR ? 2 : 4;
        const sourceNode = nodes.find((node) => node.id === edge.source);

        if (sourceNode?.data?.hasTriggerRules || sourceNode?.data?.isMetricCard) {
            minlen += 1;
        }

        if(isYesNoEdge(edgeType)) {
            const targetNode = nodes.find((node) => node.id === edge.target);

            /* create dummy node and edge to force condition Yes->No order on dagre algorithm (it will be remove after dagre.layout) */
            /* but only if target node is not a condition node */
            if(edgeType === NO && targetNode.nodeType !== CONDITION) {
                attachDummyNodeAndEdge(graph, graphDepth, sourceNode.path, edge.target);
            }
        }

        graph.setEdge(edge.source, edge.target, { minlen });
    }

    dagre.layout(graph);

    // --- Post-layout adjustment to center the START node ---
    // 1. Find the START node ID
    const startNodeId = nodes.find((node) => node.nodeType === START)?.id;

    if (startNodeId) {
        // 2. Get the START node's calculated position and dimensions
        const startNode = graph.node(startNodeId);
        const startNodeX = startNode.x;
        const startNodeWidth = startNode.width;
        const startNodeCenterX = startNodeX + startNodeWidth / 2;

        // 3. Define the container center (use container width or graph width whichever is smaller)
        const graphWidth = graph.graph().width;
        const containerCenter = containerWidth ? Math.min(containerWidth / 2, graphWidth / 2) : graphWidth / 2;

        // 4. Calculate the horizontal offset to center the START node's center point
        const offsetX = containerCenter - startNodeCenterX;

        // 5. Translate all nodes by the offset
        graph.nodes().forEach((nodeId) => {
            const node = graph.node(nodeId);
            node.x += offsetX;
        });

        // 6. Also translate edge points if they exist
        graph.edges().forEach((edge) => {
            const edgeData = graph.edge(edge);
            if (edgeData.points) {
                edgeData.points.forEach((point) => {
                    point.x += offsetX;
                });
            }
        });
    }

    const graphNodes = graph.nodes()
        .filter((node) => !node.includes('_dummy'))
        .map((node) => {
            // remove the x and y from the node since we need to set them in position { x, y }
            const { x, y, ...rest } = graph.node(node);

            return {
                id: node,
                ...rest,
                position: { x, y },
            };
        });

    alignNodesXPositions(graphNodes);

    const calculatedGraph = graph.graph();

    return {
        height: calculatedGraph.height,
        width: calculatedGraph.width,
        nodes: graphNodes,
    };
}

export function alignNodesXPositions (graphNodes) {
    const originalXByNodeId = new Map(
        graphNodes.map((node) => [node.id, node.position.x]),
    );

    graphNodes.sort((a, b) => a.path.length - b.path.length);
    graphNodes.forEach((node) => {
        const parentNodePath = node.path.slice(0, -1);
        const parentNode = graphNodes.find((node) => node.path === parentNodePath);

        if(parentNode) {
            if(parentNode.nodeType === CONDITION) {
                const yesPath = `${parentNode.path}${NODES_PATH_MAP[YES]}`;

                if (node.path === yesPath) {
                    // current node is after yes edge
                    const noPath = `${parentNode.path}${NODES_PATH_MAP[NO]}`;
                    const noNode = graphNodes.find((node) => node.path === noPath);
                    const yesNode = node;

                    if(noNode) {
                        const parentOriginalX = originalXByNodeId.get(parentNode.id) ?? parentNode.position.x;
                        const yesNodeOriginalX = originalXByNodeId.get(yesNode.id) ?? yesNode.position.x;
                        const noNodeOriginalX = originalXByNodeId.get(noNode.id) ?? noNode.position.x;

                        // set x position for yes and no nodes to the max distance from the condition node
                        const yesNodeDist = Math.abs(parentOriginalX - yesNodeOriginalX);
                        const noNodeDist = Math.abs(parentOriginalX - noNodeOriginalX);
                        const maxDistance = Math.max(yesNodeDist, noNodeDist);

                        yesNode.position.x = parentNode.position.x - maxDistance;
                        noNode.position.x = parentNode.position.x + maxDistance;
                    }
                }
            } else {
                node.position.x = parentNode.position.x;
            }
        }
    });
}

export function getNodesByOrder (nodes, edges) {
    if (nodes?.length === 0 || edges?.length === 0) {
        return [];
    }

    const startNode = nodes.find((node) => node.nodeType === START);
    if (!startNode) {
        return [];
    }
    setNodePath(startNode, nodes, edges);
    /* remove deatached nodes if there are such */
    const connectedNodes = nodes.filter((node) => Object.hasOwn(node, 'path'));
    const graphDepth = maxBy(connectedNodes, (node) => node.path?.length)?.path?.length;

    const orderedNodes = [startNode];

    for (let i = 1; i < graphDepth + 1; i++) {
        const nodesAtDepth = connectedNodes.filter((node) => node.path?.length === i);
        nodesAtDepth.forEach((node) => {
            const edgeFrom = edges.find((edge) => edge.target === node.id);
            const parentNode = connectedNodes.find((node) => node.id === edgeFrom.source);
            const indexOfParent = orderedNodes.indexOf(parentNode);
            const indexToInsertAt = indexOfParent + (edgeFrom.data.edgeType === NO ? 2 : 1);

            orderedNodes.splice(indexToInsertAt, 0, node);
        });
    }

    return orderedNodes;
}

export function getOrderEdgesByNodes (nodes, edges) {
    const orderedEdges = [];
    nodes
        .filter((node) => node.nodeType !== START)
        .forEach((node) => {
            const edgeFrom = edges.find((edge) => edge.target === node.id);
            orderedEdges.push(edgeFrom);
        });

    return orderedEdges;
}

export function setNodePath (node, nodes, edges) {
    if (!node || nodes?.length === 0 || edges?.length === 0) return;
    if (node.nodeType !== START) {
        const edgeFrom = edges.find((edge) => edge.target === node.id);
        const parentNode = nodes.find((node) => node.id === edgeFrom.source);
        const val = NODES_PATH_MAP[edgeFrom.data.edgeType];

        node.path = `${parentNode.path}${val}`;
    } else {
        node.path = '';
    }

    const edgesTo = edges.filter((edge) => edge.source === node.id);
    const nextNodes = nodes.filter((node) => edgesTo.map((edge) => edge.target).includes(node.id));
    nextNodes.forEach((node) => setNodePath(node, nodes, edges));
}

export function getReachableNodeIds (startNodeId, edges) {
    const reachable = new Set([startNodeId]);
    const frontier = [startNodeId];
    while (frontier.length > 0) {
        const current = frontier.pop();
        const outgoing = edges.filter((edge) => edge.source === current);
        for (const edge of outgoing) {
            if (!reachable.has(edge.target)) {
                reachable.add(edge.target);
                frontier.push(edge.target);
            }
        }
    }

    return reachable;
}

export function doConditionBranchesMerge (condition, nodes, edges) {
    const edgeYes = edges.find((edge) => edge.source === condition.id && edge.data?.edgeType === YES);
    const edgeNo = edges.find((edge) => edge.source === condition.id && edge.data?.edgeType === NO);
    if (!edgeYes || !edgeNo) return false;
    const fromYes = getReachableNodeIds(edgeYes.target, edges);
    const fromNo = getReachableNodeIds(edgeNo.target, edges);
    for (const id of fromYes) {
        if (fromNo.has(id)) return true;
    }

    return false;
}

export function calculateSumOfVisitorsPerCondition (condition, nodesStaticSegmentIdsCount, nodes, edges) {
    const edgeYes = edges.find((edge) => edge.source === condition.id && edge.data.edgeType === YES);
    const edgeNo = edges.find((edge) => edge.source === condition.id && edge.data.edgeType === NO);
    const nodeYes = nodes.find((node) => node.id === edgeYes.target);
    const nodeNo = nodes.find((node) => node.id === edgeNo.target);

    const visitorsYes =
        nodeYes.nodeType === CONDITION
            ? calculateSumOfVisitorsPerCondition(nodeYes, nodesStaticSegmentIdsCount, nodes, edges)
            : nodesStaticSegmentIdsCount[nodeYes.id];

    const visitorsNo =
        nodeNo.nodeType === CONDITION
            ? calculateSumOfVisitorsPerCondition(nodeNo, nodesStaticSegmentIdsCount, nodes, edges)
            : nodesStaticSegmentIdsCount[nodeNo.id];

    const countYes = visitorsYes ?? 0;
    const countNo = visitorsNo ?? 0;

    if (doConditionBranchesMerge(condition, nodes, edges)) {
        // returning parentCount + sonOnlyCount where sonOnlyCount is max(countYes, countNo) as the sum of
        // visitors per condition avoids double counting when the same visitor appears in both segments after the merge (parent = noNode downstream).
        const parentCount = countNo;
        const sonOnlyCount = Math.max(0, countYes - countNo);

        return parentCount + sonOnlyCount;
    }

    return countYes + countNo;
}

export function createStartNode () {
    return {
        nodeType: START,
    };
}

export function createExitNode () {
    return {
        nodeType: EXIT,
    };
}

export function createLocalNodeMessage (message) {
    return {
        nodeType: MESSAGE,
        messageData: message,
        Attributes: {},
    };
}

export function createLocalNodeCondition () {
    return {
        nodeType: CONDITION,
        audienceData: {
            audience: [],
            audienceUiHint: { filters: [] },
        },
        Attributes: {},
    };
}

export function createLocalEdges (nodes, edgeTypes) {
    const edges = [];
    // Only create edges if we have at least 2 nodes
    if (nodes && nodes.length > 1) {
        for (let i = 0; i < nodes.length - 1; i++) {
            const from = nodes[i].id;
            const to = nodes[i + 1]?.id;
            const edgeType = edgeTypes && edgeTypes[i];
            if (from && to) {
                edges.push(buildEdge({ from, to, edgeType }));
            }
        }
    }

    return edges;
}

export function buildEdge ({ from, to, edgeType = REGULAR, attributes = {} }) {
    return { from, to, edgeType, attributes };
}

// Returns a 0–1 ratio for the shared rate formatter (formatRatePercent).
export function calculateMetricsPercentage (current, total) {
    return total === 0 ? 0 : current / total;
}

export function getMetricsBuilderByType (type) {
    switch (type) {
        case GUIDE:
            return ({ clicked, adopted, showGoalCompletion, interaction, total, entered }) =>
                [
                    {
                        label: $t('helpers.Eligible visitors'),
                        count: total,
                        tooltipText: $t('helpers.Some visitors may have dropped out of your segment or missed this step due to the number of days it took them to reach this step. \nThis is especially prevalent when guides are not viewed within the designated timeframe.'),
                    },
                    { label: $t('helpers.Viewed'), count: interaction },
                    {
                        label: $t('helpers.Clicked'),
                        count: clicked,
                        percentage: calculateMetricsPercentage(clicked, interaction),
                    },
                    showGoalCompletion && {
                        label: $t('helpers.Completed journey goal'),
                        tooltipText:
                            $t('helpers.Percentage of visitors who achieved the journey goal out of all visitors who entered the journey.'),
                        count: adopted,
                        percentage: calculateMetricsPercentage(adopted, entered),
                    },
                ].filter(Boolean);
        case EMAIL:
            return ({ interaction, clicked, adopted, showGoalCompletion, delivered, total, entered, sent = 0, bounce = 0, unsubscribe = 0, complaint = 0 }) => {
                // Deliverability rates use sent as the denominator to match the Email surfaces.
                const deliverabilityRate = (count) => calculateMetricsPercentage(count, sent);

                return [
                    {
                        label: $t('helpers.Eligible visitors'),
                        count: total,
                        tooltipText: $t('helpers.Some_visitors_may_have_dropped_out_of_your_segment_or_missed_this_step_due_to_the_number_of_days_it_took_them_to_reach_this_step___nThis_is_especially_prevalent_when_emails_are_not_opened_within_the_designated_timeframe_'),
                    },
                    { label: $t('helpers.Sent'), count: sent },
                    {
                        label: $t('helpers.Delivery rate'),
                        count: delivered,
                        percentage: deliverabilityRate(delivered),
                    },
                    {
                        label: $t('helpers.Open rate'),
                        count: interaction,
                        percentage: calculateMetricsPercentage(interaction, delivered),
                    },
                    {
                        label: $t('helpers.Click rate'),
                        count: clicked,
                        percentage: calculateMetricsPercentage(clicked, delivered),
                    },
                    {
                        label: $t('helpers.Bounce rate'),
                        count: bounce,
                        percentage: deliverabilityRate(bounce),
                    },
                    {
                        label: $t('helpers.Unsubscribe rate'),
                        count: unsubscribe,
                        percentage: deliverabilityRate(unsubscribe),
                    },
                    {
                        label: $t('helpers.Spam rate'),
                        count: complaint,
                        percentage: deliverabilityRate(complaint),
                    },
                    showGoalCompletion && {
                        label: $t('helpers.Completed journey goal'),
                        tooltipText:
                            $t('helpers.Percentage of visitors who achieved the journey goal out of all visitors who entered the journey.'),
                        count: adopted,
                        percentage: calculateMetricsPercentage(adopted, entered),
                    },
                ].filter(Boolean);
            };
        case START:
            return ({ total }) => [{ label: $t('helpers.Visitors entered'), count: total }];
        case EXIT:
            return ({ total, showGoalCompletion }) => [
                {
                    label: $t('helpers.Visitors exited'),
                    tooltipText: `${$t('helpers.These visitors finished the journey')} ${
                        showGoalCompletion ? $t('helpers.but didn’t achieve the goal') : ''
                    }.`,
                    count: total,
                },
            ];
        case CONDITION:
            return ({ total }) => [{ label: $t('helpers.Visitors'), count: total }];
        default:
            return () => [];
    }
}

// Node validation helpers
function validateStartNodes (nodes) {
    const startNodes = nodes.filter((node) => node.nodeType === START);

    return startNodes.length === 1;
}

function validateExitNodes (nodes) {
    const exitNodes = nodes.filter((node) => node.nodeType === EXIT);

    return exitNodes.length >= 1;
}

function validateMessageNodes (nodes) {
    const messageNodes = nodes.filter((node) => node.nodeType === MESSAGE);

    return messageNodes.every((node) => {
        const { message } = node;

        return (
            message &&
            typeof message === 'object' &&
            Object.prototype.hasOwnProperty.call(message, 'id') &&
            Object.prototype.hasOwnProperty.call(message, 'type') &&
            Object.prototype.hasOwnProperty.call(message, 'durationInDays') &&
            Object.prototype.hasOwnProperty.call(message, 'staticSegmentId') &&
            Object.prototype.hasOwnProperty.call(message, 'provider')
        );
    });
}

function validateAtLeastOneMessageNode (nodes) {
    const messageNodes = nodes.filter((node) => node.nodeType === MESSAGE);

    return messageNodes.length >= 1;
}

function validateConditionNodes (nodes) {
    const conditionNodes = nodes.filter((node) => node.nodeType === CONDITION);

    return conditionNodes.every((node) => {
        // Check that audience is a non-empty array
        const hasValidAudience = Array.isArray(node.audience) && node.audience.length > 0;

        // Check that audienceUiHint is an object with non-empty filters array
        const hasValidAudienceUiHint =
            node.audienceUiHint &&
            typeof node.audienceUiHint === 'object' &&
            Array.isArray(node.audienceUiHint.filters) &&
            node.audienceUiHint.filters.length > 0;

        return hasValidAudience && hasValidAudienceUiHint;
    });
}

// Edge validation helpers
function validateStartNodeEdges (nodes, edges) {
    const startNode = nodes.find((node) => node.nodeType === START);

    if (!startNode) return false;

    // Only one edge should originate from START node
    const edgesFromStart = edges.filter((edge) => edge.from === startNode.id);

    if (edgesFromStart.length !== 1) return false;

    // No edge should point to START node
    const edgesToStart = edges.filter((edge) => edge.to === startNode.id);

    return edgesToStart.length === 0;
}

function validateExitNodeEdges (nodes, edges) {
    const exitNodes = nodes.filter((node) => node.nodeType === EXIT);

    if (exitNodes.length === 0) return false;

    // At least one edge should point to an EXIT node
    const edgesToExit = edges.filter((edge) => exitNodes.some((exitNode) => exitNode.id === edge.to));

    if (edgesToExit.length === 0) return false;

    // No edge should originate from EXIT nodes
    const edgesFromExit = edges.filter((edge) => exitNodes.some((exitNode) => exitNode.id === edge.from));

    return edgesFromExit.length === 0;
}

function validateMessageNodeEdges (nodes, edges) {
    const messageNodes = nodes.filter((node) => node.nodeType === MESSAGE);

    return messageNodes.every((messageNode) => {
        // Each MESSAGE node should have at least one incoming edge
        const hasIncomingEdge = edges.some((edge) => edge.to === messageNode.id);
        // Each MESSAGE node should have at least one outgoing edge
        const hasOutgoingEdge = edges.some((edge) => edge.from === messageNode.id);

        return hasIncomingEdge && hasOutgoingEdge;
    });
}

function validateConditionNodeEdges (nodes, edges) {
    const conditionNodes = nodes.filter((node) => node.nodeType === CONDITION);
    const exitNodes = nodes.filter((node) => node.nodeType === EXIT);

    return conditionNodes.every((conditionNode) => {
        // Each CONDITION node should have at least one incoming edge
        const hasIncomingEdge = edges.some((edge) => edge.to === conditionNode.id);
        // Each CONDITION node should have at least one outgoing edge
        const hasOutgoingEdge = edges.some((edge) => edge.from === conditionNode.id);

        // Check that no CONDITION node connects to more than one EXIT node
        const edgesToExitFromCondition = edges.filter(
            (edge) => edge.from === conditionNode.id && exitNodes.some((exitNode) => exitNode.id === edge.to),
        );
        const connectsToMultipleExits = edgesToExitFromCondition.length > 1;

        return hasIncomingEdge && hasOutgoingEdge && !connectsToMultipleExits;
    });
}

function validateEdgeReferences (nodes, edges) {
    const nodeIds = new Set(nodes.map((node) => node.id));

    return edges.every((edge) => {
        // Every edge 'from' should reference an existing node
        const fromExists = nodeIds.has(edge.from);
        // Every edge 'to' should reference an existing node
        const toExists = nodeIds.has(edge.to);

        return fromExists && toExists;
    });
}

export function isValidGraph (messageGraph) {
    // Basic structure validation
    if (!messageGraph || !messageGraph.nodes || !messageGraph.edges) {
        return false;
    }

    const { nodes, edges } = messageGraph;

    if (!Array.isArray(nodes) || !Array.isArray(edges) || nodes.length === 0) {
        return false;
    }

    // Node validation
    const nodeValidations = [
        validateStartNodes(nodes),
        validateExitNodes(nodes),
        validateMessageNodes(nodes),
        validateAtLeastOneMessageNode(nodes),
        validateConditionNodes(nodes),
    ];

    // Edge validation
    const edgeValidations = [
        validateStartNodeEdges(nodes, edges),
        validateExitNodeEdges(nodes, edges),
        validateMessageNodeEdges(nodes, edges),
        validateConditionNodeEdges(nodes, edges),
        validateEdgeReferences(nodes, edges),
    ];

    // All validations must pass
    return [...nodeValidations, ...edgeValidations].every((validation) => validation === true);
}

export function getReachableNodeIdsFromStart (messageGraph) {
    if (!messageGraph?.nodes?.length) {
        return new Set();
    }

    const startId = messageGraph.startNodeId
        || messageGraph.nodes.find((node) => node.nodeType === START)?.id;
    if (!startId) {
        return new Set();
    }

    const reachable = new Set([startId]);
    const queue = [startId];
    while (queue.length) {
        const nodeId = queue.shift();
        for (const edge of messageGraph.edges || []) {
            if (edge.from === nodeId && !reachable.has(edge.to)) {
                reachable.add(edge.to);
                queue.push(edge.to);
            }
        }
    }

    return reachable;
}

// Graph shape used for publish guards and orphan cleanup (sanitize + drop unreachable nodes).
export function getPublishReadyMessageGraph (messageGraph) {
    const sanitized = sanitizeMessageGraph(messageGraph);
    const reachable = getReachableNodeIdsFromStart(sanitized);
    const nodes = sanitized.nodes.filter((node) => reachable.has(node.id));
    const nodeIds = new Set(nodes.map((node) => node.id));
    const edges = sanitized.edges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));

    return {
        ...sanitized,
        nodes,
        edges,
    };
}

// Matches branching-journey-map prettifiedNodes gate: needs 2+ nodes and at least one edge.
function canRenderMessageGraphOnMap (messageGraph) {
    return (messageGraph?.nodes?.length ?? 0) > 1 && (messageGraph?.edges?.length ?? 0) > 0;
}

export function getDisplayMessageGraph (messageGraph) {
    if (!messageGraph) {
        return messageGraph;
    }

    const publishReady = getPublishReadyMessageGraph(messageGraph);
    if (canRenderMessageGraphOnMap(publishReady)) {
        return publishReady;
    }

    const sanitized = sanitizeMessageGraph(messageGraph);
    if (canRenderMessageGraphOnMap(sanitized)) {
        return sanitized;
    }

    return messageGraph;
}

// Validates the graph after removing orphan nodes/edges (ghost cleanup applied on status change).
export function isValidGraphAfterSanitize (messageGraph) {
    return isValidGraph(getPublishReadyMessageGraph(messageGraph));
}

export function extractMessagesFromMessageGraph (messageGraph) {
    if (!messageGraph || !messageGraph.nodes) {
        return [];
    }

    return messageGraph.nodes
        .filter((node) => node.nodeType === MESSAGE)
        .map((node) => node.message)
        .filter(Boolean); // Remove any undefined messages
}

export function cloneMessageGraph (originalMessageGraph, messageIdMapping) {
    if (!originalMessageGraph || !originalMessageGraph.nodes || !originalMessageGraph.edges) {
        return {
            nodes: [],
            edges: [],
        };
    }
    const clonedMessageGraph = cloneDeep(originalMessageGraph);
    clonedMessageGraph.nodes.forEach((node) => {
        if (node.nodeType === MESSAGE && node.message) {
            const originalMessageId = node.message.id;
            const newMessageId = messageIdMapping[originalMessageId];
            if (newMessageId) {
                node.message.id = newMessageId;
            }
            node.message.staticSegmentId = '';
        }
    });

    return clonedMessageGraph;
}

function getEdgeType (edge) {
    return edge.edgeType ?? get(edge, 'data.edgeType', REGULAR);
}

// Sanitize messageGraph by removing orphan edges and orphan MESSAGE/CONDITION nodes. START and EXIT nodes are never removed.
export function sanitizeMessageGraph (messageGraph) {
    if (!messageGraph) {
        return { nodes: [], edges: [] };
    }
    const { edges, nodes } = messageGraph;
    if (!Array.isArray(nodes) || !Array.isArray(edges)) {
        return {
            nodes: nodes && Array.isArray(nodes) ? cloneDeep(nodes) : [],
            edges: edges && Array.isArray(edges) ? cloneDeep(edges) : [],
            ...(messageGraph.startNodeId != null && { startNodeId: messageGraph.startNodeId }),
        };
    }

    const result = cloneDeep(messageGraph);
    let resultNodes = result.nodes;
    let resultEdges = result.edges;

    const nodeIdSet = new Set(resultNodes.map((node) => node.id));
    resultEdges = resultEdges.filter((edge) => nodeIdSet.has(edge.from) && nodeIdSet.has(edge.to));

    let prevSize = resultNodes.length + 1;
    while (resultNodes.length < prevSize) {
        prevSize = resultNodes.length;
        const nodeIdSetCurrent = new Set(resultNodes.map((node) => node.id));
        const edgesCurrent = resultEdges.filter(
            (edge) => nodeIdSetCurrent.has(edge.from) && nodeIdSetCurrent.has(edge.to),
        );
        const hasIncoming = (nodeId) => edgesCurrent.some((edge) => edge.to === nodeId);
        const hasOutgoing = (nodeId) => edgesCurrent.some((edge) => edge.from === nodeId);
        const hasYesOutgoing = (nodeId) =>
            edgesCurrent.some((edge) => edge.from === nodeId && getEdgeType(edge) === YES);
        const hasNoOutgoing = (nodeId) =>
            edgesCurrent.some((edge) => edge.from === nodeId && getEdgeType(edge) === NO);

        const keepNode = (node) => {
            if (node.nodeType === START || node.nodeType === EXIT) {
                return true;
            }
            if (node.nodeType === MESSAGE) {
                return hasIncoming(node.id) && hasOutgoing(node.id);
            }
            if (node.nodeType === CONDITION) {
                return (
                    hasIncoming(node.id) && hasYesOutgoing(node.id) && hasNoOutgoing(node.id)
                );
            }

            return true;
        };

        resultNodes = resultNodes.filter(keepNode);
    }

    const nodeIdSetAfter = new Set(resultNodes.map((node) => node.id));
    resultEdges = resultEdges.filter((edge) => nodeIdSetAfter.has(edge.from) && nodeIdSetAfter.has(edge.to));

    result.nodes = resultNodes;
    result.edges = resultEdges;

    return result;
}

export function getOrphanGraphElements (messageGraph) {
    const publishReady = getPublishReadyMessageGraph(messageGraph);
    const keptNodeIds = new Set(publishReady.nodes.map((node) => node.id));
    const keptEdgeIds = new Set(publishReady.edges.map((edge) => edge.id));

    return {
        orphanNodes: (messageGraph?.nodes || []).filter((node) => !keptNodeIds.has(node.id)),
        orphanEdges: (messageGraph?.edges || []).filter((edge) => !keptEdgeIds.has(edge.id)),
        sanitized: publishReady,
    };
}

export function isEdgeToExitNode (messageGraph, edgeId) {
    const { nodes, edges } = messageGraph;
    const edge = edges.find((edge) => edge.id === edgeId);

    if (!edge) return false;

    return nodes.some((node) => node.id === edge.to && node.nodeType === EXIT);
}

export function getAncestorsNodes (currentNode, nodes, edges) {
    if (currentNode.nodeType === START) {
        return [];
    }
    const edgesFromCurrentNode = edges.filter((edge) => edge.to === currentNode.id);
    const ancestorsNodes = [];
    edgesFromCurrentNode.forEach((edge) => {
        const ancestorNode = nodes.find((node) => node.id === edge.from);
        ancestorsNodes.push(ancestorNode, ...getAncestorsNodes(ancestorNode, nodes, edges));
    });

    return uniqBy(ancestorsNodes, 'id');
}

export function getAllNodesEligibleToConnectTo (messageGraph, givenNode) {
    const { nodes, edges } = messageGraph;
    const ancestorsNodes = getAncestorsNodes(givenNode, nodes, edges);

    /* ancestors nodes are ineligible to connect to */
    const ineligibleNodesSet = new Set(ancestorsNodes.map((node) => node.id));

    if(givenNode.nodeType === CONDITION) {
        const outgoingEdgesFromCondition = edges.filter((edge) => edge.from === givenNode.id);
        const nodesChildrenOfCondition = nodes.filter((node) => outgoingEdgesFromCondition.map(({ to }) => to).includes(node.id));

        /* node connected to same condition node is ineligible to connect to */
        nodesChildrenOfCondition.forEach((node) => {
            ineligibleNodesSet.add(node.id);
        });
    }

    const eligibleNodes = nodes.filter(
        (node) => (node.nodeType === MESSAGE || node.nodeType === CONDITION) && !ineligibleNodesSet.has(node.id) && node.id !== givenNode.id,
    );

    return eligibleNodes.filter((node) => node.nodeType !== CONDITION || getInsertionChainDepth(givenNode.id, node.id, nodes, edges) <= MAX_CONDITION_CHAIN);
}

export function shouldAddOptionToMessageDropdown (getters, option) {
    const { hasSegmentFlag } = useAuthStore();
    const hasProductLine = useProductLinesStore().hasProductLinePurchased;

    return (
        getDropdownOptionsShowIf(getters, option.showIf) &&
        (typeof option.segmentFlag === 'undefined' || hasSegmentFlag(option.segmentFlag)) &&
        (typeof option.productLine === 'undefined' || hasProductLine(...option.productLine))
    );
}

export function getDropdownOptionsShowIf (getters, key) {
    const { hasSegmentFlag } = useAuthStore();
    const hasProductLine = useProductLinesStore().hasProductLinePurchased;
    // journeys moved to Pinia (APP-157977); read activeJourney from Pinia store directly
    const { useJourneysStore } = require('@/stores/journeys.store');
    const activeJourney = useJourneysStore().getActiveJourney;

    switch (key) {
        case 'hasCreateJourneyPermission':
            return canCreateJourney({ appId: activeJourney.appId });
        case 'journeyHasMessageGraph':
            return !!activeJourney.messageGraph;
        case 'hasIntegrationsEnabled':
            return (
                (hasSegmentFlag('hubspotEmail') && hasProductLine('orchestrate', 'hubspotEmailIntegrationEnabled')) ||
                (hasSegmentFlag('marketoEmail') && hasProductLine('orchestrate', 'marketoEmailIntegrationEnabled')) ||
                hasProductLine('orchestrate', 'eloquaEmailIntegrationEnabled')
            );
        default:
            return true;
    }
}

export function getDropdownOptionsDisabled (getters, key) {
    switch (key) {
        case 'isMaxMessagesPassed': {
            // journeys migrated to Pinia (APP-157977)
            const { useJourneysStore } = require('@/stores/journeys.store');

            return useJourneysStore().getMessages.length >= MAX_MESSAGES;
        }
        default:
            return false;
    }
}

export function getDropdownOptionsDescription (getters, key) {
    switch (key) {
        case 'maxMessagesDescription':
            return getDropdownOptionsDisabled(getters, 'isMaxMessagesPassed')
                ? `You've reached the ${MAX_MESSAGES} messages limit for this journey. To add more, please delete an existing message.`
                : '';
        default:
            return '';
    }
}

export function formatMessageDropdownOption (getters, option) {
    const { items, ...optionWithoutItems } = option;
    const formatted = {
        ...optionWithoutItems,
        id: option.label,
        disabled: getDropdownOptionsDisabled(getters, option.disabledIf) || !!option.disabled,
        description: getDropdownOptionsDescription(getters, option.description),
    };

    if (!Array.isArray(items) || items.length === 0) {
        return formatted;
    }

    const visibleItems = items
        .filter((item) => shouldAddOptionToMessageDropdown(getters, item))
        .map((item) => formatMessageDropdownOption(getters, item));

    return visibleItems.length ? { ...formatted, items: visibleItems } : formatted;
}

export function isYesNoEdge (edgeType) {
    return [YES, NO].includes(edgeType);
}

function countConsecutiveConditions (nodeId, chainMap, conditionIds, depthCache = {}) {
    if (depthCache[nodeId] !== undefined) return depthCache[nodeId];
    if (!conditionIds.has(nodeId)) return 0;

    const connectedConditions = (chainMap[nodeId] || []).filter((id) => conditionIds.has(id));
    const chainLengths = connectedConditions.map((id) => countConsecutiveConditions(id, chainMap, conditionIds, depthCache));

    depthCache[nodeId] = 1 + Math.max(0, ...chainLengths);

    return depthCache[nodeId];
}

export function getInsertionChainDepth (sourceId, targetId, nodes, edges) {
    const conditionIds = new Set(nodes.filter((node) => node.nodeType === CONDITION).map((node) => node.id));
    const parentMap = {};
    const childMap = {};

    edges.forEach((edge) => {
        if (!parentMap[edge.to]) parentMap[edge.to] = [];
        parentMap[edge.to].push(edge.from);
        if (!childMap[edge.from]) childMap[edge.from] = [];
        childMap[edge.from].push(edge.to);
    });

    return countConsecutiveConditions(sourceId, parentMap, conditionIds) + countConsecutiveConditions(targetId, childMap, conditionIds);
}
